// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RUNTIME } from '../../native/platform.js';
import { useApp } from '../../store/AppContext.js';

// The Server URL editor for the login/register screens.
//
// The web app is served same-origin behind nginx and always uses the relative
// `/api` base, so it has no reason to show this. The packaged native shells
// (Tauri desktop, Capacitor iOS/Android) load from their own webview origin and
// MUST be told where the sync-server lives, so this lets the user set/override
// it. The value is dispatched to app state (SET_SYNC_URL), which persists it via
// the shared `hushold_syncurl` storage so it survives reloads and logout.
export function ServerUrlField() {
  const { t } = useTranslation(['auth', 'common']);
  const { state, dispatch } = useApp();
  const [open, setOpen] = useState(false);

  // Web never needs a configurable server — same-origin nginx handles it.
  if (RUNTIME === 'web') return null;

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs text-text-subtle hover:text-text-muted transition-colors"
      >
        {open ? '▾' : '▸'} {t('server.settings')}
      </button>

      {open && (
        <div className="mt-2">
          <label className="block text-sm text-text-muted mb-1">{t('server.syncServerUrl')}</label>
          <input
            type="url"
            inputMode="url"
            value={state.auth.syncUrl}
            onChange={(e) => dispatch({ type: 'SET_SYNC_URL', syncUrl: e.target.value.trim() })}
            placeholder={t('server.urlPlaceholder')}
            className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle font-mono"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <p className="text-xs text-text-subtle mt-1">
            {t('server.syncServerNote')}
          </p>
        </div>
      )}
    </div>
  );
}
